import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";
import { useCockpitAuth } from "@/lib/cockpitAuth";

export default function CockpitLogin() {
    const { login } = useCockpitAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [totp, setTotp] = useState("");
    const [needsTotp, setNeedsTotp] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const res = await login(email.trim().toLowerCase(), password, totp);
            if (res.requiresTotp) {
                setNeedsTotp(true);
            } else {
                navigate("/cockpit");
            }
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                    "Identifiants invalides ou code TOTP incorrect."
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="cockpit-login"
            className="min-h-screen bg-[#070d18] text-[#f4ecd8] font-sans-modern flex relative overflow-hidden"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 18% 12%, rgba(212,175,55,0.55) 0px, transparent 38%), radial-gradient(circle at 84% 86%, rgba(212,175,55,0.45) 0px, transparent 44%)",
                }}
            />

            {/* Left aside — institutional badge */}
            <aside className="hidden lg:flex w-[44%] relative border-r border-white/[0.06] p-16 flex-col justify-between">
                <Link to="/" data-testid="cockpit-logo-link" className="flex items-center gap-4 hover:opacity-80 transition">
                    <img
                        src="/brand/icon-gold.png"
                        alt=""
                        className="h-9 w-auto opacity-95"
                    />
                    <div className="flex flex-col leading-none">
                        <span
                            className="font-serif text-[18px] tracking-[0.32em] uppercase"
                            style={{ fontWeight: 400 }}
                        >
                            XV Cockpit
                        </span>
                        <span className="mt-2 text-[10px] tracking-[0.42em] uppercase text-[#d4af37]/85">
                            Strategic Operations · Côte d'Azur
                        </span>
                    </div>
                </Link>

                <div className="relative">
                    <h1
                        className="font-serif text-[40px] leading-[1.05] mb-8 text-[#f4ecd8]"
                        style={{ fontWeight: 300 }}
                    >
                        Cockpit confidentiel{" "}
                        <em className="not-italic text-[#d4af37]">
                            des opérations.
                        </em>
                    </h1>
                    <p className="max-w-md text-[14px] leading-[1.9] text-[#f4ecd8]/70 font-light">
                        Espace réservé aux associés et collaborateurs du
                        cabinet. Toute connexion fait l'objet d'un
                        enregistrement horodaté.
                    </p>
                    <div className="mt-12 flex items-center gap-3 text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/70">
                        <ShieldCheck strokeWidth={1.2} size={14} />
                        TOTP · Session 24h · Audit log
                    </div>
                </div>

                <div className="text-[10px] tracking-[0.32em] uppercase text-[#f4ecd8]/30">
                    XV Capital Partners · Strategic Advisory
                </div>
            </aside>

            {/* Right — form */}
            <section className="relative flex-1 flex items-center justify-center p-8 lg:p-16">
                <Link
                    to="/"
                    data-testid="cockpit-back-home"
                    className="absolute top-6 right-6 lg:top-8 lg:right-8 inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#f4ecd8]/50 hover:text-[#d4af37] transition z-10"
                >
                    <ArrowLeft strokeWidth={1.2} size={14} />
                    Retour à l'accueil
                </Link>
                <div className="w-full max-w-[420px]">
                    <div className="overline text-[#d4af37] mb-6 text-[10px] tracking-[0.36em]">
                        Authentification sécurisée
                    </div>
                    <h2
                        className="font-serif text-[34px] md:text-[40px] leading-[1.05] mb-12"
                        style={{ fontWeight: 300 }}
                    >
                        Accès cockpit.
                    </h2>

                    <form onSubmit={onSubmit} className="space-y-7">
                        <div>
                            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                Identifiant
                            </label>
                            <input
                                data-testid="cockpit-login-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={needsTotp}
                                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-3 text-[15px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/30 transition-colors duration-500"
                                placeholder="contact@xv-capitalpartners.com"
                                autoComplete="username"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                Mot de passe
                            </label>
                            <input
                                data-testid="cockpit-login-password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={needsTotp}
                                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-3 text-[15px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/30 transition-colors duration-500"
                                autoComplete="current-password"
                            />
                        </div>
                        {needsTotp && (
                            <div>
                                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                    Code TOTP
                                </label>
                                <input
                                    data-testid="cockpit-login-totp"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]{6}"
                                    maxLength={6}
                                    required
                                    value={totp}
                                    onChange={(e) =>
                                        setTotp(e.target.value.replace(/\D/g, ""))
                                    }
                                    className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-3 text-[20px] font-serif tracking-[0.5em] text-[#f4ecd8] placeholder-[#f4ecd8]/30 transition-colors duration-500"
                                    placeholder="000000"
                                    autoFocus
                                />
                                <p className="mt-3 text-[12px] text-[#f4ecd8]/45 leading-relaxed">
                                    Saisissez le code à 6 chiffres généré par
                                    votre application d'authentification.
                                </p>
                            </div>
                        )}

                        {error && (
                            <div
                                data-testid="cockpit-login-error"
                                className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            data-testid="cockpit-login-submit"
                            disabled={busy}
                            className="group w-full mt-4 inline-flex items-center justify-between px-7 py-4 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.32em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {busy
                                ? "Vérification en cours…"
                                : needsTotp
                                  ? "Valider le code"
                                  : "Accéder au cockpit"}
                            <ArrowRight
                                strokeWidth={1.2}
                                size={14}
                                className="transition-transform duration-500 group-hover:translate-x-1"
                            />
                        </button>
                    </form>

                    <div className="mt-14 text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/30">
                        Accès strictement réservé · Toute tentative est journalisée
                    </div>
                </div>
            </section>
        </div>
    );
}
